import { format } from 'date-fns';
import React, { useMemo, useCallback } from 'react';
import Link from 'next/link';
import { BreakpointQueryName, Spacing, SpacingName } from '@doveit/lego';
import { NextPage } from 'next';
import Image from 'next/image';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import PageHead from '../client/components/page-head/PageHead';
import SocialShareLinks from '../client/components/social-share-links/SocialShareLinks';
import ArticleNextPrevFooterContainer from '../client/containers/article-next-prev-footer/ArticleNextPrevFooterContainer';
import { NEWS_QUERY, RELATED_NEWS_QUERY } from '../client/graphql/queries/news';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import RapidLinks from '../client/templates/common/rapid-links/RapidLinks';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { buildNotFound, buildPageGone } from '../client/utils/error/http/httpErrors';
import {
  AUTHOR_AS,
  AUTHOR_URL,
  buildArticlesURLObject, buildArticleURLObject, buildNewsSerpURLObject, goToPage, relativeToAbsoluteURL, slugIt, URLObject,
} from '../client/utils/links';
import { getBlogArticleStructuredData, getBreadcrumbStructuredData, getStructuredData } from '../data/structured';
import { ArticleType } from '../domain/articleType';
import {
  AdvFluid, AdvFluidType, AdvFluidVariant, New, Node, NodeType, Guide,
} from '../domain/graphcms';
import {
  ArticlesBreadcrumb, ARTICLE_PREVIEW_HEIGHT, ARTICLE_PREVIEW_WIDTH, DataTrackingContext,
} from '../client/constants';
import ListRelatedNewsByLocality from '../client/containers/list-related-news-by-locality/ListRelatedNewsByLocality';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import RelatedArticles, { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import { mapArticlesNodes } from '../client/graphql/mappers/articleMappers';
import { FEATURED_GUIDES_QUERY, GUIDE_QUERY } from '../client/graphql/queries/guide';
import { getLogger } from '../logger/isomorphic';
import { getRelated } from '../client/utils/article/articleUtils';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import BottomBarV2 from '../client/templates/common/bottom-bar-v2/BottomBarV2';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import AdvFluidAdapter from '../client/components/adv-fluid-adapter/AdvFluidAdapter';
import GraphCMSComponentRenderer from '../client/components/graphcms-component-renderer/GraphCMSComponentRenderer';
import { includes } from '../client/utils/graphcms/nodeUtils';
import AdvInterstitial from '../client/components/adv-interstitial/AdvInterstitial';
import { BottomBarData, getBottomBarByAdvType } from '../services/bottom-bars/bottomBars';
import * as commonStyles from '../client/templates/style';
import * as styles from '../client/templates/news/News.style';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';
import CategoryList from '../client/components/category-list/CategoryList';
import { ArticleFAQ } from '../client/components/blocks';
import { getFAQStructuredData } from '../data/structured/faq';

export interface NewsPageProps {
  news: New,
  relatedNews: Omit<RelatedArticleItem, 'onClick'>[],
  relatedGuides: Omit<RelatedArticleItem, 'onClick'>[],
  bottomBar: BottomBarData,
  guideRef?: Pick<Guide, 'id' | 'title' | 'alternateTitle'>,
}

// approximation of max image width to be requested to graphcms CDN

const INTERSECTION_OPTIONS = { threshold: 1 };

export const NewsPage: NextPage<NewsPageProps> = ({
  news,
  relatedNews,
  relatedGuides,
  bottomBar,
  guideRef,
}) => {
  const {
    id,
    title,
    alternateTitle,
    metaPreview,
    imageBlock,
    introduction,
    mainColumn,
    rightColumn,
    seo: SEO,
    faq,
    published,
    createdAt,
    localities,
    newsCategory,
  } = news;
  const newsSerpURLObject = useMemo<URLObject>(() => buildArticlesURLObject({}, ArticleType.NEWS), []);
  const articleURLObject = useMemo<URLObject>(
    () => buildArticleURLObject({ id, slug: slugIt(alternateTitle || title) }, ArticleType.NEWS),
    [alternateTitle, id, title],
  );

  const { ref: headerRef, entry: header } = useRefInView(INTERSECTION_OPTIONS);
  const { ref: relatedArticlesRef, entry: relatedArticles } = useRefInView(INTERSECTION_OPTIONS);

  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: ArticlesBreadcrumb[ArticleType.NEWS],
      urlObject: newsSerpURLObject,
    },
    {
      label: title,
      urlObject: articleURLObject,
    },
  ], [newsSerpURLObject, title, articleURLObject]);

  const goToBottomBarLink = useCallback(
    () => goToPage(bottomBar.urlObject),
    [bottomBar],
  );

  const articleURL = useMemo(
    () => relativeToAbsoluteURL(articleURLObject.as),
    [articleURLObject],
  );

  const faqItemsForStructuredData = React.useMemo(() => (faq ? faq.items.map((faqItem) => ({
    id: slugIt(faqItem.question),
    question: faqItem.question,
    answer: faqItem.answer.text,
  })) : undefined), [faq]);

  const faqItems = React.useMemo(() => (faq ? faq.items.map((faqItem) => ({
    id: slugIt(faqItem.question),
    question: faqItem.question,
    answer: faqItem.answer.markdown,
  })) : undefined), [faq]);

  return (
    <AppLayout activePage="news">
      <PageHead
        SEO={{
          ...SEO,
          canonical: articleURL,
          robots: ['max-image-preview:large'],
        }}
        type="article"
        image={metaPreview?.image?.url ? {
          url: metaPreview?.image?.url,
          width: ARTICLE_PREVIEW_WIDTH,
          height: ARTICLE_PREVIEW_HEIGHT,
        } : null}
        article={{
          section: newsCategory?.name || 'Notizie immobiliari',
          author: 'Dove.it',
          tags: SEO.keywords,
        }}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
          getBlogArticleStructuredData(news, articleURL, ArticleType.NEWS),
          faqItemsForStructuredData && getFAQStructuredData(faqItemsForStructuredData),
        )}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header>
            <div
              ref={headerRef}
              css={styles.header}
            >
              <h1 css={styles.title}>{title}</h1>
              <div css={styles.details}>
                <div css={styles.info}>
                  <span>Pubblicata il {format(new Date(published), 'dd/MM/yyyy')}</span>
                  <span>
                    {'da '}
                    <Link
                      href={AUTHOR_URL}
                      as={`${AUTHOR_AS}/redazione`}
                    >
                      Redazione
                    </Link>
                  </span>
                </div>
                {newsCategory && (
                  <div css={styles.categories}>
                    <div css={styles.divider}>
                      |
                    </div>
                    <div css={styles.categoriesLabel}>
                      Categoria:
                    </div>
                    <CategoryList items={[{ id: newsCategory.slug, label: newsCategory.name, urlObject: buildNewsSerpURLObject({ category: newsCategory.slug }) }]} />
                  </div>
                )}
              </div>
            </div>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              <article>
                {imageBlock && imageBlock.image && (
                  <div css={styles.image}>
                    <Image
                      src={imageBlock.image.url}
                      width={imageBlock.image.width}
                      height={imageBlock.image.height}
                      alt={imageBlock.imageAlt}
                    />
                  </div>
                )}
                <ArticleMarkdown>
                  {introduction}
                </ArticleMarkdown>

                <Spacing margin={[SpacingName.MEDIUM, 0, 0, 0]}>
                  <AdvInterstitial guideRef={guideRef} />
                </Spacing>

                {mainColumn.map((block: Node) => (
                  <Spacing
                    margin={[SpacingName.MEDIUM, 0, 0, 0]}
                    key={block.id}
                  >
                    <GraphCMSComponentRenderer {...block} />
                  </Spacing>
                ))}
              </article>

              {faq && (
                <Spacing margin={[SpacingName.BIG, 0, 0]}>
                  <ArticleFAQ
                    title={faq.title.markdown}
                    items={faqItems}
                  />
                </Spacing>
              )}

              {news.localities && news.localities.length === 1 && (
                <div css={styles.rapidLinks}>
                  <RapidLinks
                    withoutPad
                    locality={news.localities[0]}
                    showAgency
                    showEvaluation
                  />
                </div>
              )}

              <ArticleNextPrevFooterContainer
                articleType={ArticleType.NEWS}
                skipId={id}
                date={published}
                createdAt={createdAt}
              />

              <div css={styles.share}>
                <h4>
                  Condividi questa notizia:
                </h4>
                <SocialShareLinks
                  url={articleURL}
                  title={`${title}`}
                />
              </div>
            </PageLayout.Main>
            <PageLayout.Aside>
              <AdvFluidAdapter
                {...(rightColumn ?? {})}
                dataTrackingPrefix={DataTrackingContext.SIDE}
              />

              <div
                ref={relatedArticlesRef}
                css={styles.related}
              >
                {relatedNews.length > 0 && (
                  <RelatedArticles
                    title="News più lette"
                    items={relatedNews}
                  />
                )}
                {relatedGuides.length > 0 && (
                  <RelatedArticles
                    title="Guide più lette"
                    items={relatedGuides}
                  />
                )}
                {localities.length > 0 && (
                  <ListRelatedNewsByLocality
                    title={`Altre notizie su ${localities[0].name}`}
                    skipId={id}
                    locality={localities[0].slug}
                  />
                )}
              </div>

              <div
                $visible={relatedArticles?.boundingClientRect.y < 0}
                css={commonStyles.stickySideAdv}
                data-ref="adv-fluid-sticky-aside"
              >
                <AdvFluidAdapter
                  {...(rightColumn ?? {})}
                  dataTrackingPrefix={DataTrackingContext.SIDE_STICKY}
                />
              </div>
            </PageLayout.Aside>
          </PageLayout.Content>
          <PageLayout.Breadcrumb items={breadcrumb} />
          <BottomBarV2
            hiddenFrom={BreakpointQueryName.LG}
            visible={header?.boundingClientRect.y < 0}
            label={bottomBar.label}
            title={bottomBar.title}
            href={bottomBar.urlObject?.as}
            onClick={goToBottomBarLink}
          />
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

const logger = getLogger();

export const getServerSideProps = withGetServerSideError<NewsPageProps>(async ({ query }) => {
  if ((query.slug as string).includes('}')) {
    throw buildPageGone(`Page with slug ${query.slug} is gone`);
  }

  const { advInterstitial: { guideRef } } = await importDynamicData('pages/news.yml');
  const graphQLClient = initGraphQLClient();
  const { data: { guide: guideRefObj } } = await graphQLClient.query<{ guide: Guide }>({
    query: GUIDE_QUERY,
    variables: {
      id: guideRef.id,
    },
  });
  const { data } = await graphQLClient.query<{ new: New }>({
    query: NEWS_QUERY,
    variables: {
      id: query.id,
      metaPreviewWidth: ARTICLE_PREVIEW_WIDTH,
      metaPreviewHeight: ARTICLE_PREVIEW_HEIGHT,
    },
  });

  const news = data.new;
  assertNotNullOrUndefined(news, buildNotFound(`News with id "${query.id}" not found`));

  const [relatedNews, relatedGuides] = await Promise.all([
    graphQLClient
      .query({
        query: RELATED_NEWS_QUERY,
        variables: {
          skipId: news.id, date: news.published, count: 4, createdAt: news.createdAt,
        },
      })
      .then(({ data: related }) => getRelated(related.older, related.newer, 4))
      .then((related) => mapArticlesNodes(related, ArticleType.NEWS))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering related news');
        return [];
      }),
    graphQLClient
      .query({ query: FEATURED_GUIDES_QUERY, variables: { count: 4 } })
      .then(({ data: guides }) => mapArticlesNodes(guides.featured, ArticleType.GUIDE))
      .catch((err) => {
        logger.warn({ err }, 'Error while quering feature guides');
        return [];
      }),
  ]);

  const slug = slugIt(news.alternateTitle || news.title);

  if (query.slug !== slug) {
    return {
      redirect: {
        destination: buildArticleURLObject({
          id: news.id,
          slug,
        }, ArticleType.NEWS).as,
        permanent: true,
      },
    };
  }

  const mainColumn = [...(news.mainColumn ?? [])];

  if (!includes(news.mainColumn, NodeType.ADV_FLUID)) {
    mainColumn.splice(1, 0, {
      __typename: NodeType.ADV_FLUID,
      id: Date.now().toString(),
      advFluidType: AdvFluidType.SELL_PROPERTY,
      variant: AdvFluidVariant.VARIANT_1,
    } as AdvFluid);
  }

  const bottomBar = getBottomBarByAdvType(news.rightColumn?.advFluidType ?? AdvFluidType.SELL_PROPERTY);

  return {
    props: {
      id: query.id,
      news: {
        ...news,
        mainColumn,
      },
      relatedNews,
      relatedGuides,
      bottomBar,
      guideRef: guideRefObj
        ? {
          id: guideRefObj.id,
          title: guideRefObj.title,
          alternateTitle: guideRefObj.alternateTitle,
        }
        : null,
    },
  };
});

export default withError(NewsPage);
